/**
 * Events deputies sign up for: I'll be there / Standby / I won't be there (a full count disappears), and paging
 * the standby list by text and email.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, PeoplePicker, when } from './common.tsx';

export const ANSWERS = [
  { k: 'there', label: 'I’ll be there' },
  { k: 'standby', label: 'Standby' },
  { k: 'no', label: 'I won’t be there' },
] as const;
const ANSWER_LABEL: Record<string, string> = { there: 'There', standby: 'Standby', no: 'Not going' };
const range = (e: any) => `${when(e.starts_at, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${e.ends_at ? ` – ${new Date(e.ends_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}`;
const count = (n: number, need: number) => (need > 0 ? `${n} of ${need}` : `${n}`);

/**
 * The three-way slider. Positions whose count is full are gone (they can't be picked); the deputy's own answer
 * stays even if the count is full.
 */
export function AnswerSlider({ e, onDone, compact }: { e: any; onDone: () => void; compact?: boolean }) {
  const a = useAction();
  const choices: string[] = e.choices ?? ['there', 'standby', 'no'];
  const shown = ANSWERS.filter((x) => choices.includes(x.k));
  const idx = shown.findIndex((x) => x.k === e.my_answer);
  const pick = (k: string) => a.run(async () => (await api.post(`/api/events/${e.id}/answer`, { answer: k === e.my_answer ? null : k }), onDone()));
  return (
    <div className={`ev-slider${compact ? ' compact' : ''}`}>
      <div className="ev-track" role="radiogroup" aria-label={`Your answer for ${e.title}`} style={{ gridTemplateColumns: `repeat(${shown.length}, 1fr)` }}>
        {idx >= 0 && <span className={`ev-thumb ${e.my_answer}`} style={{ width: `${100 / shown.length}%`, transform: `translateX(${idx * 100}%)` }} aria-hidden />}
        {shown.map((x) => (
          <button key={x.k} type="button" role="radio" aria-checked={e.my_answer === x.k} className={e.my_answer === x.k ? 'on' : ''} disabled={a.busy || e.status !== 'open'} onClick={() => pick(x.k)}>
            {x.label}
          </button>
        ))}
      </div>
      {(e.full_there || e.full_standby) && (
        <div className="small muted">
          {e.full_there && e.my_answer !== 'there' ? 'Every spot is taken. ' : ''}
          {e.full_standby && e.my_answer !== 'standby' ? 'The standby list is full.' : ''}
        </div>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}

export function EventsPage() {
  const { navigate } = useRouter();
  const [scope, setScope] = useState('upcoming');
  const r = useLoad(() => api.get<any>(`/api/events?scope=${scope}`), [scope]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Agency"
        title="Events"
        sub="Details that need deputies. Tell command staff whether you’ll be there, on standby, or not."
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => navigate(at('/events/new'))}>
              Post an event
            </button>
          )
        }
      />
      <div className="seg-ctl" role="tablist" aria-label="Which events">
        {(
          [
            ['upcoming', 'Coming up'],
            ['past', 'Past'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={scope === k} className={scope === k ? 'on' : ''} onClick={() => setScope(k)}>
            {l}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.events.length ? (
        <EmptyState icon="calendar" title="No events" />
      ) : (
        <div className="ev-list">
          {r.data.events.map((e: any) => (
            <div key={e.id} className="card ev-card">
              <button type="button" className="ev-head" onClick={() => navigate(at(`/events/${e.id}`))}>
                <strong>{e.title}</strong>
                <span className="small muted">
                  {range(e)}
                  {e.place ? ` · ${e.place}` : ''}
                </span>
              </button>
              <div className="ev-counts small">
                <span>
                  <strong>{count(e.n_there, e.need_there)}</strong> there
                </span>
                <span>
                  <strong>{count(e.n_standby, e.need_standby)}</strong> standby
                </span>
                {e.status === 'canceled' && <Badge tone="bad">Canceled</Badge>}
              </div>
              {e.invited && e.status === 'open' && scope === 'upcoming' && <AnswerSlider e={e} onDone={r.reload} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventForm({ init, units, people, onSave, busy, schools, schoolOnly }: { init: Record<string, any>; units: any[]; people: any[]; onSave: (v: Record<string, any>) => void; busy: boolean; schools?: any[]; schoolOnly?: boolean }) {
  const [f, setF] = useState<Record<string, any>>(init);
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const iso = (v: string) => (v ? new Date(v).toISOString() : null);
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ ...f, startsAt: iso(f.startsAt), endsAt: iso(f.endsAt), reportAt: iso(f.reportAt), needThere: Number(f.needThere || 0), needStandby: Number(f.needStandby || 0) });
      }}
    >
      <Card title={schoolOnly || f.schoolId ? 'The school detail' : 'The event'}>
        {(schoolOnly || (schools && schools.length > 0)) && (
          <div className="grid-3">
            <label>
              School
              <select required={schoolOnly} value={f.schoolId ?? ''} onChange={(e) => set('schoolId', e.target.value || null)}>
                <option value="">{schoolOnly ? 'Pick the school…' : 'Not a school detail'}</option>
                {(schools ?? []).map((x: any) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="check" style={{ alignSelf: 'end' }}>
              <input type="checkbox" checked={!!f.offDuty} onChange={(e) => set('offDuty', e.target.checked)} /> Off-duty (paid by the school / event)
            </label>
            <label>
              Pay
              <input value={f.pay ?? ''} onChange={(e) => set('pay', e.target.value)} placeholder="$35/hr, paid by the school" />
            </label>
          </div>
        )}
        <div className="grid-3">
          <label style={{ gridColumn: 'span 2' }}>
            Title
            <input required value={f.title ?? ''} onChange={(e) => set('title', e.target.value)} placeholder="Homecoming parade" />
          </label>
          <label>
            Place
            <input value={f.place ?? ''} onChange={(e) => set('place', e.target.value)} />
          </label>
          <label>
            Starts
            <input type="datetime-local" required value={f.startsAt ?? ''} onChange={(e) => set('startsAt', e.target.value)} />
          </label>
          <label>
            Ends
            <input type="datetime-local" value={f.endsAt ?? ''} onChange={(e) => set('endsAt', e.target.value)} />
          </label>
          <label>
            Report by
            <input type="datetime-local" value={f.reportAt ?? ''} onChange={(e) => set('reportAt', e.target.value)} />
          </label>
          <label>
            Deputies needed there
            <input type="number" min="0" value={f.needThere ?? ''} onChange={(e) => set('needThere', e.target.value)} placeholder="0 = no limit" />
          </label>
          <label>
            On standby
            <input type="number" min="0" value={f.needStandby ?? ''} onChange={(e) => set('needStandby', e.target.value)} placeholder="0 = no limit" />
          </label>
        </div>
        <label>
          Details (uniform, where to park, who’s in charge)
          <textarea rows={3} value={f.details ?? ''} onChange={(e) => set('details', e.target.value)} />
        </label>
      </Card>
      <Card title="Who’s invited">
        <label className="check">
          <input type="checkbox" checked={f.inviteAll} onChange={(e) => set('inviteAll', e.target.checked)} /> Everyone
        </label>
        {!f.inviteAll && (
          <div className="stack sm">
            <div className="small strong">Shifts, units or divisions</div>
            <div className="bwc-checks">
              {units.map((u) => (
                <label key={u.id} className="check">
                  <input type="checkbox" checked={(f.inviteUnits as string[]).includes(u.id)} onChange={() => set('inviteUnits', (f.inviteUnits as string[]).includes(u.id) ? f.inviteUnits.filter((x: string) => x !== u.id) : [...f.inviteUnits, u.id])} /> {u.name}
                </label>
              ))}
            </div>
            <PeoplePicker people={people} value={f.invitePeople} onChange={(v) => set('invitePeople', v)} label="People" />
          </div>
        )}
      </Card>
      <div>
        <button className="btn primary" disabled={busy}>
          Save
        </button>
      </div>
    </form>
  );
}

export function NewEvent() {
  const { navigate } = useRouter();
  const meta = useLoad(() => api.get<any>('/api/events/meta'));
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="Events" title="Post an event" sub="Deputies answer on their Home. When a count is full, that answer goes away." />
      <ErrorBox error={a.error} />
      <EventForm
        init={{ inviteAll: true, inviteUnits: [], invitePeople: [] }}
        units={meta.data?.units ?? []}
        people={meta.data?.people ?? []}
        schools={meta.data?.schools ?? []}
        busy={a.busy}
        onSave={(v) => a.run(async () => navigate(at(`/events/${(await api.post<{ id: string }>('/api/events', v)).id}`), true))}
      />
    </div>
  );
}

/** An SRO posts an off-duty detail at a school (ballgame, dance, graduation). */
export function NewSchoolDetail() {
  const { navigate } = useRouter();
  const meta = useLoad(() => api.get<any>('/api/events/meta'));
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="After-school details" title="Post an after-school detail" sub="Everyone invited sees it under Needs your attention and signs up. When the spots are full, the sign-up closes." />
      <ErrorBox error={a.error} />
      {meta.data && (
        <EventForm
          init={{ inviteAll: true, inviteUnits: [], invitePeople: [], offDuty: true, needStandby: 0 }}
          units={meta.data.units}
          people={meta.data.people}
          schools={meta.data.schools}
          schoolOnly
          busy={a.busy}
          onSave={(v) => a.run(async () => navigate(at(`/events/${(await api.post<{ id: string }>('/api/events', v)).id}`), true))}
        />
      )}
    </div>
  );
}

const wday = (iso: string) => new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
const hm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');

/** SRO section: after-school details — sign up, and the roster of who's working what. */
export function SchoolDetailsPage() {
  const { navigate } = useRouter();
  const [tab, setTab] = useState<'signup' | 'roster'>(new URLSearchParams(location.search).get('tab') === 'roster' ? 'roster' : 'signup');
  const list = useLoad(() => api.get<any>('/api/events?kind=school&scope=upcoming'), []);
  const roster = useLoad(() => (tab === 'roster' ? api.get<any>('/api/events/roster?kind=school') : Promise.resolve(null)), [tab]);
  const days = new Map<string, any[]>();
  for (const e of roster.data?.events ?? []) {
    const k = new Date(e.starts_at).toLocaleDateString('en-CA');
    days.set(k, [...(days.get(k) ?? []), e]);
  }
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Schools"
        title="After-school details"
        sub="Off-duty work at school events. Sign up for the ones you want; the roster shows who’s working what."
        actions={
          <div className="row gap-sm">
            <button className="btn no-print" onClick={() => window.print()}>
              Print
            </button>
            {list.data?.canManage && (
              <button className="btn primary no-print" onClick={() => navigate(at('/schools/details/new'))}>
                Post a detail
              </button>
            )}
          </div>
        }
      />
      <div className="seg-ctl no-print" role="tablist" aria-label="View">
        <button type="button" role="tab" aria-selected={tab === 'signup'} className={tab === 'signup' ? 'on' : ''} onClick={() => setTab('signup')}>
          Sign up
        </button>
        <button type="button" role="tab" aria-selected={tab === 'roster'} className={tab === 'roster' ? 'on' : ''} onClick={() => setTab('roster')}>
          Who’s working
        </button>
      </div>
      {tab === 'signup' ? (
        list.error ? (
          <ErrorBox error={list.error} />
        ) : !list.data ? (
          <Spinner />
        ) : !list.data.events.length ? (
          <EmptyState icon="calendar" title="No school details coming up" />
        ) : (
          <div className="ev-list">
            {list.data.events.map((e: any) => (
              <div key={e.id} className="card ev-card">
                <button type="button" className="ev-head" onClick={() => navigate(at(`/events/${e.id}`))}>
                  <strong>
                    {e.school_name} · {e.title}
                  </strong>
                  <span className="small muted">
                    {range(e)}
                    {e.place ? ` · ${e.place}` : ''}
                    {e.pay ? ` · ${e.pay}` : ''}
                    {e.off_duty ? ' · off-duty' : ''}
                  </span>
                </button>
                <div className="ev-counts small">
                  <span>
                    <strong>{count(e.n_there, e.need_there)}</strong> working
                  </span>
                  {e.need_standby > 0 && (
                    <span>
                      <strong>{count(e.n_standby, e.need_standby)}</strong> standby
                    </span>
                  )}
                </div>
                {e.invited && e.status === 'open' && <AnswerSlider e={e} onDone={list.reload} />}
              </div>
            ))}
          </div>
        )
      ) : roster.error ? (
        <ErrorBox error={roster.error} />
      ) : !roster.data ? (
        <Spinner />
      ) : !roster.data.events.length ? (
        <EmptyState icon="calendar" title="No school details in the next 60 days" />
      ) : (
        <div className="sd-roster">
          {[...days].map(([k, evs]) => (
            <section key={k} className="sd-day">
              <h3>{wday(evs[0].starts_at)}</h3>
              {evs.map((e: any) => (
                <div key={e.id} className="card sd-ev">
                  <div className="sd-when">
                    <strong>{hm(e.starts_at)}</strong>
                    {e.ends_at && <span className="small muted">to {hm(e.ends_at)}</span>}
                  </div>
                  <div className="sd-what">
                    <a href={at(`/events/${e.id}`)} onClick={(ev) => (ev.preventDefault(), navigate(at(`/events/${e.id}`)))}>
                      <strong>
                        {e.school_name} · {e.title}
                      </strong>
                    </a>
                    <div className="small muted">{[e.place, e.pay, e.report_at && `report ${hm(e.report_at)}`].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="sd-who">
                    {e.working.length ? (
                      <ul className="plain">
                        {e.working.map((p: any) => (
                          <li key={p.id}>
                            {p.name}
                            {p.call ? <span className="muted small"> ({p.call})</span> : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="muted small">No one yet</span>
                    )}
                    {e.need_there > e.working.length && <Badge tone="warn">{e.need_there - e.working.length} open</Badge>}
                    {e.standby.length > 0 && <div className="small muted">Standby: {e.standby.map((p: any) => p.name).join(', ')}</div>}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

const toLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function EventDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/events/${id}`), [id]);
  const a = useAction();
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState('');
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { event: e, answers, pages, pagedMe, canManage, units, people, schools } = r.data;
  const by = (k: string) => answers.filter((x: any) => x.answer === k);
  const reply = (rep: string) => a.run(async () => (await api.post(`/api/events/${id}/page-reply`, { reply: rep }), r.reload()));
  return (
    <div className="ops stack">
      <PageHeader
        crumb={e.school_id ? 'After-school details' : 'Events'}
        title={`${e.school_name ? `${e.school_name} · ` : ''}${e.title}`}
        sub={`${range(e)}${e.place ? ` · ${e.place}` : ''}${e.report_at ? ` · report by ${new Date(e.report_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''} · posted by ${e.created_by_name}`}
        actions={
          canManage &&
          e.status === 'open' && (
            <div className="row gap-sm">
              <button className="btn" onClick={() => setEditing(!editing)}>
                {editing ? 'Close' : 'Edit'}
              </button>
              <button className="btn ghost" disabled={a.busy} onClick={() => window.confirm('Cancel this event? Everyone signed up is told.') && a.run(async () => (await api.post(`/api/events/${id}`, { cancel: true }), r.reload()))}>
                Cancel event
              </button>
            </div>
          )
        }
      />
      <ErrorBox error={a.error} />
      {e.status === 'canceled' && <Notice tone="bad">This event was canceled.</Notice>}
      {pagedMe && (
        <div className="dh-important" role="alert">
          <div>
            <span className="dh-imp-tag">Standby page</span>
            <strong>You’re needed now.</strong>
          </div>
          <div className="row gap-sm">
            <button className="btn primary" disabled={a.busy} onClick={() => reply('coming')}>
              On my way
            </button>
            <button className="btn" disabled={a.busy} onClick={() => reply('cannot')}>
              Can’t make it
            </button>
          </div>
        </div>
      )}
      {editing && (
        <EventForm
          init={{ ...e, startsAt: toLocal(e.starts_at), endsAt: toLocal(e.ends_at), reportAt: toLocal(e.report_at), needThere: e.need_there, needStandby: e.need_standby, inviteAll: e.invite_all, inviteUnits: e.invite_units, invitePeople: e.invite_people, schoolId: e.school_id, offDuty: e.off_duty, pay: e.pay }}
          schools={schools}
          schoolOnly={!!e.school_id}
          units={units}
          people={people}
          busy={a.busy}
          onSave={(v) => a.run(async () => (await api.post(`/api/events/${id}`, v), setEditing(false), r.reload()))}
        />
      )}
      {(e.pay || e.off_duty) && (
        <Notice tone="info">
          {e.off_duty ? 'Off-duty detail' : 'On-duty detail'}
          {e.pay ? ` · ${e.pay}` : ''}
        </Notice>
      )}
      {e.details && (
        <Card title="Details">
          <p className="pre">{e.details}</p>
        </Card>
      )}
      {e.invited && e.status === 'open' && (
        <Card title="Your answer">
          <AnswerSlider e={e} onDone={r.reload} />
        </Card>
      )}
      <div className="grid-3-cards">
        {(['there', 'standby', 'no'] as const).map((k) => (
          <Card key={k} title={`${k === 'there' ? 'There' : k === 'standby' ? 'Standby' : 'Not going'} · ${k === 'there' ? count(e.n_there, e.need_there) : k === 'standby' ? count(e.n_standby, e.need_standby) : e.n_no}`}>
            {by(k).length ? (
              <ul className="plain">
                {by(k).map((x: any) => (
                  <li key={x.user_id}>
                    {x.name}
                    {x.call_number ? <span className="muted small"> ({x.call_number})</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">No one.</p>
            )}
          </Card>
        ))}
      </div>
      {canManage && e.status === 'open' && (
        <Card title="Page the standby list">
          <p className="small">Sends a text and an email to everyone on standby ({e.n_standby}), and puts it on their Home. Each one answers On my way or Can’t make it from the link.</p>
          <label>
            Message
            <input value={msg} onChange={(ev) => setMsg(ev.target.value)} placeholder={`You’re needed at ${e.title}${e.place ? `, ${e.place}` : ''}. Report now.`} />
          </label>
          <div>
            <button className="btn danger" disabled={a.busy || !e.n_standby} onClick={() => window.confirm(`Page all ${e.n_standby} on standby now?`) && a.run(async () => (await api.post(`/api/events/${id}/page`, { message: msg || undefined }), setMsg(''), r.reload()))}>
              Page standby now
            </button>
          </div>
          {pages.map((p: any) => (
            <div key={p.id} className="ev-page">
              <div className="small">
                <strong>{when(p.sent_at)}</strong> · {p.sent_by_name}: “{p.message}”
              </div>
              <ul className="plain small">
                {p.sent_to.map((t: any) => (
                  <li key={t.userId}>
                    {t.name}: {t.reply === 'coming' ? <Badge tone="ok">On my way</Badge> : t.reply === 'cannot' ? <Badge tone="bad">Can’t make it</Badge> : <Badge tone="warn">No answer yet</Badge>} <span className="muted">({t.channels.filter((c: string) => c !== 'bluehq').join(', ') || 'BlueHQ only'})</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Card>
      )}
      <p className="small muted">Answers: {Object.entries(ANSWER_LABEL).map(([k, l]) => `${l} ${k === 'there' ? e.n_there : k === 'standby' ? e.n_standby : e.n_no}`).join(' · ')}</p>
    </div>
  );
}

/** Home: events coming up with the slider right there. */
export function HomeEvents({ events, canPost, onChange }: { events: any[]; canPost: boolean; onChange: () => void }) {
  const { navigate } = useRouter();
  if (!events.length && !canPost) return null;
  return (
    <Card
      title="Events — are you going?"
      actions={
        <a href={at('/events')} className="small" onClick={(ev) => (ev.preventDefault(), navigate(at('/events')))}>
          {canPost ? 'Post or manage events' : 'All events'}
        </a>
      }
    >
      {events.length === 0 ? (
        <p className="muted small">Nothing coming up.</p>
      ) : (
        <div className="stack sm">
          {events.map((e) => (
            <div key={e.id} className="ev-home">
              <a href={at(`/events/${e.id}`)} onClick={(ev) => (ev.preventDefault(), navigate(at(`/events/${e.id}`)))}>
                <strong>{e.title}</strong>
              </a>
              <span className="small muted">
                {' '}
                · {range(e)}
                {e.place ? ` · ${e.place}` : ''} · {count(e.n_there, e.need_there)} there, {count(e.n_standby, e.need_standby)} standby
              </span>
              <AnswerSlider e={e} onDone={onChange} compact />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
